// Propsのデフォルト設定を無効にする
/* eslint-disable react/require-default-props */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import languageIcon from '@/assets/language.svg';

import { GLOSSARY_LANGUAGES } from '../constants/glossaryLanguages';

import styles from './GlossaryLangPanel.module.scss';

/**
 * プロパティ
 */
type Props = {
  /**
   * true のとき、タイトル行をクリックして言語一覧を開閉できるアコーディオン形式にする。
   * false（デフォルト）のとき、一覧を常時展開した静的パネルを表示する。
   */
  collapsible?: boolean;
};

/**
 * 用語集の対応言語コード一覧パネル。
 * - 静的モード（`collapsible` 省略時）: 空状態画面で常時展開
 * - アコーディオンモード（`collapsible`）: アップロードダイアログでトグル表示
 */
export const GlossaryLangPanel = React.memo(
  ({ collapsible = false }: Props) => {
    const { t } = useTranslation();
    /** アコーディオンの開閉状態。静的モードでは初期値 true で固定。 */
    const [isOpen, setIsOpen] = useState(!collapsible);

    /**
     * アコーディオンの開閉をトグルする。
     */
    const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

    /**
     * パネルヘッダー（アイコン + タイトル）。
     * 静的・アコーディオン両モードで共通。
     */
    const panelHeader = useMemo(
      () => (
        <div className={styles['info-panel-header']}>
          <img
            src={languageIcon}
            alt=""
            className={styles['info-panel-header-icon']}
          />
          <span className={styles['info-panel-title']}>
            {t('glossary.supportedLanguageList')}
          </span>
        </div>
      ),
      [t],
    );

    /** 言語コード一覧の JSX。静的・アコーディオン両モードで共通。 */
    const langList = useMemo(
      () => (
        <>
          <p className={styles['info-panel-desc']}>
            {t('glossary.supportedLanguagesHeading')}
          </p>
          <div className={styles['lang-code-list']}>
            {GLOSSARY_LANGUAGES.map(({ code, i18nKey }) => {
              const label = t(
                `language.${i18nKey}` as Parameters<typeof t>[0],
                { defaultValue: code },
              );

              return (
                <span
                  key={code}
                  className={styles['lang-code-item']}
                  title={`${code} ${label}`}
                >
                  <code className={styles['lang-code-badge']}>{code}</code>
                  <span className={styles['lang-code-label']}>{label}</span>
                </span>
              );
            })}
          </div>
        </>
      ),
      [t],
    );

    if (collapsible) {
      return (
        <div className={styles['info-panel']}>
          <button
            type="button"
            className={styles['info-panel-toggle']}
            onClick={handleToggle}
          >
            {panelHeader}
            <span className={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
          </button>
          {isOpen && (
            <div className={styles['info-panel-body']}>{langList}</div>
          )}
        </div>
      );
    }

    return (
      <div className={styles['info-panel']}>
        {panelHeader}
        {langList}
      </div>
    );
  },
);
